import {
  Dispatch,
  MutableRefObject,
  SetStateAction,
  createContext,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ShipSpeed } from '@rich-products-corp/cc-custom-components/dist/components/PageSections/Ecommerce/checkout/ship-speed-description';
import { Address } from '@rich-products-corp/providers-richproducts';
import type { State } from '@rich-products-corp/react-ecomm-ui/dist/components/PageSections/Ecommerce/forms/address';
import { LineItem } from '@rich-products-corp/react-ecomm-ui/dist/types/cart/LineItem';
import { PaymentMethodResult } from '@stripe/stripe-js';
import { CheckoutStep, useCheckoutSteps } from '../hooks/use-checkout-steps';
import { GiftMessageForm } from '../schemas/giftMessage.schema';
import { PaymentForm } from '../schemas/payment.schema';
import { ShippingForm } from '../schemas/shipping.schema';
import {
  DEFAULT_CUTOFF_TIME,
  DEFAULT_TIMEZONE,
  DateOverride,
  ShipDateTableEntry,
  TransitRule,
  parseDateOverrides,
} from '../../../../helpers/dateHelpers';

export type DeliveryMethod = {
  id: string;
  title: string;
  turnaround: string;
  price: string;
};

export type OrderShippingPlanning = {
  address: Address;
  quantity: number;
  shippingMethod?: string;
  shippingDate?: string;
  oneMessagePerRecipients?: boolean;
  giftMessageLine1?: string;
  giftMessageLine2?: string;
  giftMessageLine3?: string;
  giftMessageLine4?: string;
  giftMessageLine5?: string;
};

export type OrderDeliveryItem = {
  lineItem: LineItem;
  quantity: number;
  address: Address | null;
  shippingPlanning: OrderShippingPlanning[];
};

type CheckoutformData = {
  shipping?: ShippingForm;
  giftMessage?: GiftMessageForm;
  payment?: PaymentForm;
  addressKey?: string;
};

type CheckoutContextState = {
  checkoutData: CheckoutformData;
  steps: CheckoutStep[];
  currentStep: number;
  deliveryMethods: DeliveryMethod[];
  paymentMethodResult: PaymentMethodResult;
  carouselRef: MutableRefObject<any>;
  provinces: State[];
  sameAddress: boolean;
  shipSpeedData: ShipSpeed[];
  shippingInfo: string;
  unavailabledDates: any[];
  beyondTodayDate: number;
  undatePattern: any[];
  dateOverrides: DateOverride[];
  shippingMode?: 'Single Address' | 'Multiple Addresses';
  orderDelivery?: OrderDeliveryItem[];
  allowShippingSpeedByProduct?: boolean;
  allowShippingDateByProduct?: boolean;
  shippingDateSpeed?: string;
  cutoffTime: string;
  cutoffTimezone: string;
  shipDateTable: ShipDateTableEntry[];
  transitDaysByMethod: TransitRule[];
};

type Handlers = {
  nextStep: () => void;
  prevStep: () => void;
  //TODO: Remove after refactor payment alternatives
  setPaymentMethodResult: Dispatch<SetStateAction<PaymentMethodResult>>;
  setProvinces: Dispatch<SetStateAction<unknown[]>>;
  setCheckoutData: Dispatch<SetStateAction<CheckoutformData>>;
  setSameAddress: Dispatch<SetStateAction<boolean>>;
  setShippingMode: Dispatch<SetStateAction<'Single Address' | 'Multiple Addresses'>>;
  setOrderDelivery: Dispatch<SetStateAction<OrderDeliveryItem[]>>;
  setAllowShippingSpeedByProduct: Dispatch<SetStateAction<boolean>>;
  setAllowShippingDateByProduct: Dispatch<SetStateAction<boolean>>;
  setCurrentStep: Dispatch<SetStateAction<number>>;
};

const CheckoutContext = createContext<[CheckoutContextState, Handlers, Dispatch<SetStateAction<number>>] | undefined>(
  undefined,
);

/**
 * Parse MM/DD/YYYY string to YYYY-MM-DD format
 * @param dateStr - Date in MM/DD/YYYY format (e.g., "01/28/2026")
 * @returns YYYY-MM-DD format (e.g., "2026-01-28")
 */
function parseMMDDYYYY(dateStr: string): string {
  if (!dateStr || typeof dateStr !== 'string') return '';
  
  const match = dateStr.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!match) return '';
  
  const [, month, day, year] = match;
  return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
}

export const CheckoutProvider = ({
  children,
  states: s,
  shippingMethods,
  shipSpeedData,
  shippingInfo,
  unavailabledDates,
  beyondTodayDate,
  undatePattern,
  dateOverrides = [],
  cutoffTime = DEFAULT_CUTOFF_TIME,
  cutoffTimezone = DEFAULT_TIMEZONE,
  shipDateTable = [],
  transitDaysByMethod = [],
}) => {
  const [sameAddress, setSameAddress] = useState<boolean>(true);
  const [shippingMode, setShippingMode] = useState<'Single Address' | 'Multiple Addresses'>('Single Address');
  const [checkoutData, setCheckoutData] = useState<CheckoutformData>();
  const [shippingDateSpeed, setShippingDateSpeed] = useState<string>('standard');
  const carouselRef = useRef(null);
  const [paymentMethodResult, setPaymentMethodResult] = useState<PaymentMethodResult>();
  const [orderDelivery, setOrderDelivery] = useState<OrderDeliveryItem[]>([]);

  const [allowShippingSpeedByProduct, setAllowShippingSpeedByProduct] = useState<boolean>(false);
  const [allowShippingDateByProduct, setAllowShippingDateByProduct] = useState<boolean>(false);

  const { steps, currentStep, setCurrentStep, nextStep, prevStep } = useCheckoutSteps(carouselRef);

  const defaultProvinces = s ? Object.keys(s).map((key) => ({ key, value: s[key] })) : undefined;

  const [provinces, setProvinces] = useState(defaultProvinces);

  // Parse unavailabledDates from MM/DD/YYYY format
  // This ensures dates entered in Studio are disabled correctly in the datepicker
  const convertedUnavailabledDates = useMemo(() => {
    if (!unavailabledDates || !Array.isArray(unavailabledDates)) {
      return [];
    }
    return unavailabledDates
      .map((date) => {
        if (date?.unavailableDate) {
          const dateStr = parseMMDDYYYY(date.unavailableDate);
          if (dateStr) {
            return {
              ...date,
              unavailableDate: `${dateStr}T00:00:00.000Z`,
            };
          }
        }
        return null;
      })
      .filter(Boolean);
  }, [unavailabledDates]);

  const parsedDateOverrides = useMemo(() => parseDateOverrides(dateOverrides), [dateOverrides]);

  const handlers = useMemo(
    () => ({
      nextStep,
      prevStep,
      setPaymentMethodResult,
      setProvinces,
      setCheckoutData,
      setSameAddress,
      setShippingMode,
      setOrderDelivery,
      setCurrentStep,
      setAllowShippingSpeedByProduct,
      setAllowShippingDateByProduct,
      setShippingDateSpeed,
    }),
    [nextStep, prevStep],
  );

  return (
    <CheckoutContext.Provider
      value={[
        {
          steps,
          currentStep,
          deliveryMethods: shippingMethods,
          paymentMethodResult, //TODO: Remove after refactor payment alternatives
          carouselRef,
          provinces,
          checkoutData,
          sameAddress,
          shipSpeedData,
          shippingInfo,
          unavailabledDates: convertedUnavailabledDates,
          beyondTodayDate,
          undatePattern,
          dateOverrides: parsedDateOverrides,
          shippingMode,
          orderDelivery,
          allowShippingSpeedByProduct,
          allowShippingDateByProduct,
          shippingDateSpeed,
          cutoffTime,
          cutoffTimezone,
          shipDateTable,
          transitDaysByMethod,
        },
        handlers,
        setCurrentStep,
      ]}
    >
      {children}
    </CheckoutContext.Provider>
  );
};

export const useCheckoutContext = () => {
  const context = useContext(CheckoutContext);

  if (context === undefined) {
    throw new Error(`useCheckoutContext must be used within a CheckoutProvider`);
  }

  return context;
};
